import { inject, Injectable, signal } from '@angular/core';
import { ReportResultColumnDto } from '@http/clients/reporting-app/reporting-api-proxies';
import { ColumnState, GridApi } from 'ag-grid-enterprise';
import { ObjectExtensionsService } from 'ngx-lib';
import { GridService } from './grid.service';

export interface ReportColumnType extends ReportResultColumnDto {
    isSelected?: boolean;
}

@Injectable()
export class ReportColumnVisibilityService {
    readonly availableColumns = signal<ReportColumnType[]>([]);
    readonly columnVisibilityState = signal<ReportColumnType[]>([]);
    readonly lastAppliedState = signal<ReportColumnType[]>([]);

    private readonly gridService = inject(GridService);
    private readonly objectExtensionService = inject(ObjectExtensionsService);

    initFromApiColumns(cols: ReportResultColumnDto[], hiddenColumns: string[] = []): void {
        const hiddenSet = new Set(hiddenColumns);
        const availableColumns = cols.map((col) => ({
            ...col,
            isSelected: col.isFrozen ? true : !hiddenSet.has(col.propertyName)
        }));

        this.availableColumns.set(availableColumns);
        this.setColumnVisibilityState(availableColumns);
        this.lastAppliedState.set(this.objectExtensionService.clone(availableColumns) as ReportColumnType[]);
    }

    reset(): void {
        this.availableColumns.set([]);
        this.columnVisibilityState.set([]);
        this.lastAppliedState.set([]);
    }

    setColumnVisibilityState(columns: ReportColumnType[]): void {
        this.columnVisibilityState.set(columns);
    }

    getVisibilityRecord(): Record<string, boolean> {
        return this.toVisibilityRecord(this.columnVisibilityState());
    }

    getHiddenColumns(): string[] {
        const columns = this.lastAppliedState().length ? this.lastAppliedState() : this.columnVisibilityState();
        return columns.filter((column) => !column.isSelected).map((column) => column.propertyName);
    }

    updateGridColumnVisibility(columns: ReportColumnType[]): void {
        const columnsCloned = this.objectExtensionService.clone(columns) as ReportColumnType[];
        const api = this.gridService.getGridApi();
        if (!api) {
            throw new Error('The grid does not exist');
        }

        this.applyColumnsToGrid(api, columnsCloned);
        this.setColumnVisibilityState(columnsCloned);
        this.lastAppliedState.set(this.objectExtensionService.clone(columnsCloned) as ReportColumnType[]);
        this.gridService.adjustColumnWidths();
    }

    applyToGrid(): void {
        const api = this.gridService.getGridApi();
        const columns = this.lastAppliedState();
        if (!api || !columns.length) return;

        this.applyColumnsToGrid(api, columns);
    }

    syncFromGrid(): void {
        const api = this.gridService.getGridApi();
        if (!api) return;

        const columnState: ColumnState[] = api.getColumnState();
        const availableColumns = this.availableColumns();

        const updatedColumns = availableColumns.map((column) => {
            const state = columnState.find(
                (s) => s.colId === column.propertyName || this.resolveColId(api, column.propertyName) === s.colId
            );
            return {
                ...column,
                isSelected: column.isFrozen ? true : state ? !state.hide : column.isSelected
            };
        });

        this.setColumnVisibilityState(updatedColumns);
        this.lastAppliedState.set(this.objectExtensionService.clone(updatedColumns) as ReportColumnType[]);
    }

    private applyColumnsToGrid(api: GridApi, columns: ReportColumnType[]): void {
        const columnState: ColumnState[] = api.getColumnState();

        for (const state of columnState) {
            const columnFound = columns.find(
                (column) =>
                    column.propertyName === state.colId || this.resolveColId(api, column.propertyName) === state.colId
            );
            if (columnFound) {
                state.hide = !columnFound.isSelected;
            }
        }

        api.applyColumnState({ state: columnState });
    }

    private resolveColId(api: GridApi, propertyName: string): string | undefined {
        const gridCols = api.getColumns() ?? [];
        return gridCols.find((c) => c.getColId() === propertyName || c.getColDef().field === propertyName)?.getColId();
    }

    private toVisibilityRecord(columns: ReportColumnType[]): Record<string, boolean> {
        return columns.reduce(
            (acc, column) => {
                acc[column.propertyName] = column.isSelected ?? true;
                return acc;
            },
            {} as Record<string, boolean>
        );
    }
}
